import React, { useEffect, useRef, useState, useMemo } from "react"
import SampleCard from './SampleCard'
import CollectionCard from './CollectionCard'
import ThemeProviderWrapper from './ThemeProviderWrapper'
import Sample from "../models/sample"
import Collection from "../models/collection"
import sampleStore from "../stores/sampleStore"

import { CircularProgress, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation, Mousewheel } from 'swiper/modules';

import { observer, Provider } from "mobx-react"

const CarouselWrapper = styled('div')({
  overscrollBehavior: 'none',
  touchAction: 'pan-y',
  position: 'relative',
});

const SwiperContainer = styled('div')(({ theme }) => ({
  width: '100%',
  padding: '0',
  boxSizing: 'border-box',
  minHeight: '200px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '-20px'
}));

const NavigationButtons = styled('div')(({ theme }) => ({
  alignSelf: 'flex-end',
  display: 'flex',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  zIndex: '2',
  top: '-43px',
  right: '0',
  position: 'absolute'
}));

const StyledSwiper = styled(Swiper)(({ theme }) => ({
  width: '100%',
  '& .swiper-pagination-bullet': {
    background: "rgb(51, 51, 51)",
    opacity: 0.5,
  },
  '& .swiper-pagination-bullet-active': {
    opacity: 1,
  },
  paddingBottom: '30px !important', // Space for pagination dots
}));

const StyledSlide = styled(SwiperSlide)(({ theme }) => ({
  width: 'auto',
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.secondary.main,
  backgroundColor: 'transparent',
  fontSize: '1.4rem',
  transition: 'all 0.2s',
  '&:hover, &:focus, &:active, &.Mui-focusVisible': {
    backgroundColor: 'transparent',
    color: theme.palette.secondary.dark,
  },
  '&.Mui-disabled': {
    color: theme.palette.action.disabled,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.secondary.main}`,
    outlineOffset: '2px',
  },
}));

const NavigationButton = ({ icon, onClick, disabled }) => (
  <StyledIconButton
    onClick={onClick}
    disabled={disabled}
    size="small"
    aria-label={`Navigate carousel to ${icon} slide`}
  >
    <i className={`fa fa-chevron-${icon}`} aria-hidden="true"></i>
  </StyledIconButton>
);

const stores = {
  sampleStore,
}

const Carousel = observer(({ samples, collections }) => {
  const wrapperRef = useRef(null);
  const swiperRef = useRef(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const processedSamples = useMemo(() =>
    samples ? samples.map((sample) => new Sample(sample)) : []
  , [samples]);

  const processedCollections = useMemo(() =>
    collections ? collections.map((collection) => new Collection(collection)) : []
  , [collections]);

  useEffect(() => {
    setIsHydrated(true);
    sampleStore.samples = samples.map(
      (sample) => new Sample(sample)
    )
    sampleStore.loadFromLocalStorage();
    // Disable overscroll behavior
    document.body.style.overscrollBehaviorX = 'none';

    const preventNavigation = (e) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
      }
    };

    // Add global event listener
    window.addEventListener('wheel', preventNavigation, { passive: false });

    return () => {
      // Clean up
      document.body.style.overscrollBehaviorX = '';
      window.removeEventListener('wheel', preventNavigation);
    };
  }, []);

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const handleSlideChange = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    const totalSlides = swiper.slides.length;
    const slidesPerView = swiper.params.slidesPerView;
    const activeIndex = swiper.activeIndex;
    setIsEnd(activeIndex + Math.floor(slidesPerView) >= totalSlides);
  };

  const renderSamples = () => {
    const showSampleSelect = (sampleStore.showSampleSelect || sampleStore.showSequencer)

    return processedSamples.map((sample, index) => (
      <StyledSlide key={`${sample.slug}-${index}`}>
        <SampleCard sample={sample} showSampleSelect={showSampleSelect}/>
      </StyledSlide>
    ));
  }

  const renderCollections = () => {
    return processedCollections.map((collection, index) => (
      <StyledSlide key={`${collection.slug}-${index}`}>
        <CollectionCard collection={collection} />
      </StyledSlide>
    ));
  }

  const renderSlides = () => {
    if (samples && samples.length > 0) {
      return renderSamples()
    } else if (collections && collections.length > 0) {
      return renderCollections()
    }
  }

  return (
    <Provider {...stores}>
      <ThemeProviderWrapper>
        <CarouselWrapper ref={wrapperRef}>
        <SwiperContainer>
          {!isHydrated ? (
            <CircularProgress />
          ) : (
            <>
              <NavigationButtons>
                <NavigationButton
                  icon="left"
                  onClick={handlePrev}
                  disabled={isBeginning}
                />
                <NavigationButton
                  icon="right"
                  onClick={handleNext}
                  disabled={isEnd}
                />
              </NavigationButtons>
              <StyledSwiper
                modules={[Pagination, Navigation, Mousewheel]}
                spaceBetween={20}
                slidesPerView={4.2}
                slidesPerGroup={4}
                slidesOffsetBefore={10}
                slidesOffsetAfter={10}
                preventClicks={false}
                speed={500}
                mousewheel={{
                  forceToAxis: true,
                  sensitivity: 1,
                  thresholdDelta: 20,
                  thresholdTime: 150,
                  invert: false,
                }}
                pagination={{
                  clickable: true,
                }}
                navigation={false}
                breakpoints={{
                  0: {
                    slidesPerView: 2.2,
                    slidesPerGroup: 2,
                    spaceBetween: 10,
                  },
                  601: {
                    slidesPerView: 4.2,
                    slidesPerGroup: 4,
                    spaceBetween: 20,
                  },
                }}
                onSlideChange={handleSlideChange}
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                  handleSlideChange(swiper);
                }}
                on={{
                  slideChangeTransitionStart: function () {
                    const swiper = this;
                    const currentPage = Math.floor(swiper.activeIndex / swiper.params.slidesPerGroup);
                    const targetIndex = currentPage * swiper.params.slidesPerGroup;
                    if (swiper.activeIndex !== targetIndex) {
                      swiper.slideTo(targetIndex, 0);
                    }
                  },
                }}
              >
                <div aria-live="polite" aria-atomic="true">
                  {renderSlides()}
                </div>
              </StyledSwiper>
            </>
          )}
        </SwiperContainer>
      </CarouselWrapper>
    </ThemeProviderWrapper>
  </Provider>
  );
});

export default Carousel;